import { Stack, Tab, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { grey } from '@mui/material/colors';
import { DataGrid } from '@mui/x-data-grid';
import React from 'react';
import { useSelector } from 'react-redux';
import { CutterHistory } from '../../types/cutterHistory';
import { RootState } from '../../store';
import * as datagrid from './cutterHistory.DatagridColumns';
import HelpIcon from '../../help/HelpIcon';
import TabsWithExport from '../../common/TabsWithExport';
import { useDataGridPageForSelection } from '../../styles/DataGridConfig';

export interface CutterDetailsHistoryConnexionProps {
    cutter: CutterHistory;
    isVersalis: boolean;
    horizontalTabValue: string;
    onHorizontalTabChange: (event: React.SyntheticEvent, newValue: string) => void;
    commonDataGridProps: any;
    horizontalTabsProps: any;
    tabPanelSx: any;
    selectedRowId?: number | null;
}

export const CutterDetailsHistoryConnexion: React.FC<CutterDetailsHistoryConnexionProps> = ({
    cutter,
    isVersalis,
    horizontalTabValue,
    onHorizontalTabChange,
    commonDataGridProps,
    horizontalTabsProps,
    tabPanelSx,
    selectedRowId
}) => {
    const showStatusReportsOnly = useSelector((state: RootState) => state.persistedReportFilterReducer.showStatusReportsOnly);

    const [reportsPage, setReportsPage] = useDataGridPageForSelection(cutter.reports, selectedRowId);
    const [lisaPage, setLisaPage] = useDataGridPageForSelection(cutter.lisa, selectedRowId);
    const [dashboardPage, setDashboardPage] = useDataGridPageForSelection(cutter.dashboard, selectedRowId);

    // Filter reports based on the switch state
    const filteredReports = React.useMemo(() => {
        if (!showStatusReportsOnly) {
            return cutter.reports;
        }
        return cutter.reports.filter(report =>
            report.zipFileName?.toLowerCase().includes('status')
        );
    }, [cutter.reports, showStatusReportsOnly]);

    // Derive active dataset + ExcelJS columns from the visible tab
    const activeExport = React.useMemo(() => {
        const dataToExcelCols = (data: any[]): Array<{ key: string; header: string }> =>
            data.length === 0 ? [] : Object.keys(data[0]).map(k => ({ key: k, header: k }));
        switch (horizontalTabValue) {
            case 'cutterReports':
                return { data: filteredReports, columns: dataToExcelCols(filteredReports), label: `${cutter.serialNumber} zip-uploads` };
            case 'lisaImports':
                return { data: cutter.lisa, columns: dataToExcelCols(cutter.lisa), label: `${cutter.serialNumber} lisa-imports` };
            case 'dashboard':
                return { data: cutter.dashboard, columns: dataToExcelCols(cutter.dashboard), label: `${cutter.serialNumber} dashboard` };
            default:
                return null;
        }
    }, [horizontalTabValue, filteredReports, cutter]);

    return (
        <TabContext value={horizontalTabValue}>
            <TabsWithExport
                value={horizontalTabValue}
                onChange={onHorizontalTabChange}
                {...horizontalTabsProps}
                sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525'
                }}
                aria-label="Connection tabs"
                exportData={activeExport ? { ...activeExport, exportType: 'connexion' } : null}>
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Zip uploads ({filteredReports.length})</Typography>
                        <HelpIcon helpContext="details-zip-uploads" placement="right" size="small" />
                    </Stack>
                } value='cutterReports' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Lisa imports ({cutter.lisa.length})</Typography>
                        <HelpIcon helpContext="details-lisa-imports" placement="right" size="small" />
                    </Stack>
                } value='lisaImports' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Dashboard ({cutter.dashboard.length})</Typography>
                        <HelpIcon helpContext="details-dashboard" placement="right" size="small" />
                    </Stack>
                } value='dashboard' />
            </TabsWithExport>
            <TabPanel value='cutterReports' sx={{ p: 0 }}>
                <DataGrid
                    {...commonDataGridProps}
                    rows={filteredReports}
                    columns={datagrid.getReportsColumns(filteredReports)}
                    {...(horizontalTabValue === 'cutterReports' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={reportsPage}
                    onPaginationModelChange={setReportsPage}
                />
            </TabPanel>
            <TabPanel value='lisaImports' sx={{ p: 0 }}>
                <DataGrid
                    {...commonDataGridProps}
                    rows={cutter.lisa}
                    columns={datagrid.lisaColumns}
                    {...(horizontalTabValue === 'lisaImports' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={lisaPage}
                    onPaginationModelChange={setLisaPage}
                />
            </TabPanel>
            <TabPanel value='dashboard' sx={{ p: 0 }}>
                {/* 
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    {'Dashboard is not updated since 24th of January 2026 because of datasource change. Being fixed soon'}
                </Alert>
                
                */}
                <DataGrid
                    {...commonDataGridProps}
                    rows={cutter.dashboard}
                    columns={datagrid.dashboardColumns}
                    {...(horizontalTabValue === 'dashboard' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={dashboardPage}
                    onPaginationModelChange={setDashboardPage}
                />
            </TabPanel>
        </TabContext>
    );
};

export default CutterDetailsHistoryConnexion;